'use client';
import { Markdown } from '@repo/ui/src/components/Markdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import { headingClasses, displayClasses, textAlignClasses } from '@repo/ui/src/utils/styleUtils';
import { ToutContentButtons } from './_toutContentButtons';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import React from 'react';

const lightHeadingClasses = {
  h1: displayClasses.h1Light,
  h2: displayClasses.h2Light,
  h3: headingClasses.h3,
  h4: headingClasses.h4,
  h5: headingClasses.h5,
  h6: headingClasses.h6,
  h7: headingClasses.h7,
};

export const ToutContent = ({
  align = 'center',
  heading,
  headingLight,
  headerVariation,
  headingStyleVariant,
  lilHeading,
  highlight,
  description,
  flexible,
  fullWidth,
  buttons = [],
  buttonSize,
  buttonNegative = false,
  dark,
  divider,
  forceDivider,
  lineClamp = 0,
  grillRecommendation,
  narrowContent,
  youtubeLink,
  narrowDividerMargin,
  icon,
  className,
  style = {},
}: ToutContentProps) => {
  const hasButtons = buttons?.length || youtubeLink;
  const HeaderEl = headerVariation?.headerVariation || 'h2';

  return (
    <div className={className} style={style}>
      <div className={classNames(dark && 'tw-text-neutral-100 [&_p]:tw-text-neutral-100')}>
        {icon?.image && (
          <LazyLoadImage
            imgComponent={
              <img height="32px" src={buildImageSrc(icon, 32, 1, 1)} alt={icon.image.name} />
            }
            className={classNames('icon tw-mb-4', textAlignClasses[align])}
          />
        )}

        {lilHeading && (
          <p
            className={classNames(
              'tw-font-display46 tw-text-3xl tw-tracking-wide tw-uppercase tw-mb-2 tw-w-full',
              dark && 'dark',
              flexible && 'flexible',
              narrowContent && 'tw-w-[288px] tw-max-w-full',
              textAlignClasses[align],
            )}
          >
            {lilHeading}
          </p>
        )}

        {(heading || headingLight) && (
          <HeaderEl
            className={classNames(
              'heading',
              dark && 'tw-text-neutral-100',
              flexible && 'flexible',
              textAlignClasses[align],
              headingStyleVariant === 'h1'
                ? displayClasses[headingStyleVariant]
                : headingClasses[headingStyleVariant ?? HeaderEl],
              !!(highlight || description || forceDivider || hasButtons) &&
                !narrowDividerMargin &&
                'tw-mb-6',
              (forceDivider || (divider && description)) && narrowDividerMargin
                ? 'tw-mt-4'
                : 'tw-mt-6',
            )}
          >
            {headingLight && (
              <span
                className={classNames(
                  headingStyleVariant && lightHeadingClasses[headingStyleVariant],
                  'tw-font-display46 tw-font-normal tw-text-inherit',
                  align === 'center' ? 'tw-flex tw-justify-center' : 'tw-block',
                )}
              >
                {headingLight}
              </span>
            )}
            <span className="tw-hidden">&nbsp;</span>
            {heading}
          </HeaderEl>
        )}

        {(forceDivider || (divider && description)) && (
          <div
            className={classNames(
              'tw-flex',
              align === 'left' && 'tw-justify-start',
              align === 'right' && 'tw-justify-end',
              align === 'center' && 'tw-justify-center',
            )}
          >
            <hr
              className={classNames(
                'tw-h-1 tw-w-12 tw-bg-brand-300 tw-border-none tw-mb-6 divider',
              )}
            />
          </div>
        )}
        <div className="bottom-content">
          {highlight && (
            <p
              className={classNames(
                'tw-font-bold',
                dark && 'tw-text-neutral-100 tw-grid',
                flexible && 'flexible',
                narrowContent &&
                  'tw-w-[320px] md:tw-w-auto md:tw-max-w-[350px] motion-reduce:tw-w-auto motion-reduce:tw-max-w-[350px]',
                lineClamp > 0 && 'tw-line-clamp-4',
                fullWidth && 'tw-max-w-[776px]',
                !!description && 'tw-mb-6',
                textAlignClasses[align],
              )}
            >
              {highlight}
            </p>
          )}

          {description && (
            <Markdown
              components={{
                ol: ({ children }) => (
                  <ol className={classNames('tw-list-none tw-m-0 tw-pt-2')}>{children}</ol>
                ),
                li: ({ children }) => (
                  <li className={classNames('tw-mb-4 tw-relative')}>{children}</li>
                ),
                ul: ({ children }) => (
                  <ul
                    className={classNames(
                      align === 'left' && 'tw-text-left',
                      align === 'right' && 'tw-text-right',
                      align === 'center' && 'tw-text-center',
                    )}
                  >
                    {children}
                  </ul>
                ),
              }}
              className={classNames(
                'description body',
                textAlignClasses[align],
                dark && 'tw-text-neutral-100',
                narrowContent &&
                  'tw-w-[320px] md:tw-w-auto md:tw-max-w-[350px] motion-reduce:tw-w-auto motion-reduce:tw-max-w-[350px]',
                lineClamp > 0 && 'tw-overflow-hidden',
                fullWidth && 'lg:tw-max-w-[776px]',
                hasButtons && 'tw-mb-6',
                align === 'left' ? 'tw-ml-0' : 'tw-ml-auto',
                align === 'right' ? 'tw-mr-0' : 'tw-mr-auto',
                'tw-max-w-full lg:tw-max-w-[45ch]',
              )}
              bodySize={flexible ? 'xLarge' : 'large'}
            >
              {description}
            </Markdown>
          )}

          <ToutContentButtons
            buttons={buttons}
            dark={dark}
            align={align}
            buttonSize={buttonSize}
            grillRecommendation={grillRecommendation}
            youtubeLink={youtubeLink}
            buttonNegative={buttonNegative}
          />
        </div>
      </div>
    </div>
  );
};
